import { useState, useEffect } from "react";
import {
  Tabs,
  Tab,
  Box,
  IconButton,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import {
  Plus,
  Search,
  Settings,
  RotateCw,
  FilterX,
  X,
  Pencil,
  Wallet,
  Bell,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

const sortOptions = [
  { value: "newest", label: "Newest First" },
  { value: "oldest", label: "Oldest First" },
  { value: "supplierAZ", label: "Supplier (A-Z)" },
  { value: "supplierZA", label: "Supplier (Z-A)" },
];

// Sample data for demonstration
const inwards = [
  {
    id: "PI-1",
    poId: "PO-1",
    supplier: "Prints Bank",
    date: "15",
    month: "MAY",
    amount: 25000,
    status: "unpaid",
    challanNo: "CH-2024-001",
    paymentDue: 15000,
  },
  {
    id: "PI-2",
    poId: "PO-2",
    supplier: "Supplier 1",
    date: "16",
    month: "MAY",
    amount: 18000,
    status: "unpaid",
    challanNo: "CH-2024-002",
    paymentDue: 18000,
  },
  {
    id: "PI-3",
    poId: "PO-3",
    supplier: "Prints Bank",
    date: "10",
    month: "MAY",
    amount: 32000,
    status: "paid",
    challanNo: "CH-2024-003",
  },
  {
    id: "PI-4",
    poId: "PO-4",
    supplier: "Supplier 2",
    date: "12",
    month: "MAY",
    amount: 45000,
    status: "paid",
    challanNo: "CH-2024-004",
  },
];

const PurchaseInward = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("unpaid");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [selectedInwardForReminder, setSelectedInwardForReminder] =
    useState(null);
  const [reminderDateTime, setReminderDateTime] = useState(null);
  const [reminderDescription, setReminderDescription] = useState("");

  // Define columns based on activeTab
  const defaultColumns = [
    { id: "date", label: "Purchase Date", required: true },
    { id: "inwardId", label: "Inward ID", required: true },
    { id: "poId", label: "PO ID", required: true },
    { id: "supplier", label: "Supplier", required: true },
    { id: "challanNo", label: "Challan No", required: true },
    { id: "amount", label: "Total Amount", required: true },
    {
      id: "paymentDue",
      label: "Payment Due",
      required: activeTab === "unpaid",
    },
  ];

  const [visibleColumns, setVisibleColumns] = useState({});

  // Update visible columns when activeTab changes
  useEffect(() => {
    setVisibleColumns(
      defaultColumns.reduce((acc, col) => {
        // Only show payment due for unpaid tab
        if (col.id === "paymentDue" && activeTab === "paid") {
          return acc;
        }
        return { ...acc, [col.id]: true };
      }, {}),
    );
  }, [activeTab]);

  const [filters, setFilters] = useState({
    dateRange: "",
    sortBy: "",
  });

  const handleSearchChange = event => {
    setSearchQuery(event.target.value);
  };

  const clearSearch = () => {
    setSearchQuery("");
  };

  const handleSettingsClick = event => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseSettings = () => {
    setAnchorEl(null);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate refresh
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  const clearAllFilters = () => {
    setSearchQuery("");
    setFilters({
      dateRange: "",
      sortBy: "",
    });
  };

  const handleReminderClick = (e, inward) => {
    e.stopPropagation();
    setSelectedInwardForReminder(inward);
    setReminderModalOpen(true);
  };

  const handleSetReminder = () => {
    console.log("Setting reminder for inward:", selectedInwardForReminder?.id);
    console.log("Reminder time:", reminderDateTime);
    console.log("Description:", reminderDescription);
    setReminderModalOpen(false);
    setSelectedInwardForReminder(null);
    setReminderDateTime(null);
    setReminderDescription("");
  };

  return (
    <div className="bg-white h-full">
      {/* Header Section */}
      <div className="flex items-center justify-between px-6 py-4 border-b">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold font-outfit text-gray-800">
            Purchase Inwards
          </h1>
          <button
            className="px-4 py-2 bg-[#5652B0] text-white rounded-lg text-sm font-medium 
                     hover:bg-[#4A47A3] transition-all duration-200 shadow-sm 
                     hover:shadow-md active:transform active:scale-95 
                     flex items-center gap-2"
            onClick={() => navigate("/purchase/inward/create")}>
            <Plus size={16} className="stroke-[2]" />
            Create Purchase Inward
          </button>
        </div>

        <div className="flex items-center gap-4">
          {/* Search Bar */}
          <div className="w-[450px]">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 transform -translate-y-1/2"
                size={18}
                color="#83899F"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                placeholder="Search by Inward ID, PO ID, Supplier..."
                className="w-full h-10 pl-10 pr-10 rounded-lg border border-gray-200 
                         focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] 
                         outline-none transition-all text-gray-600 bg-white
                         hover:border-gray-300 focus:shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 
                           hover:bg-gray-100 p-1.5 rounded-full transition-all duration-200">
                  <X size={14} color="#83899F" />
                </button>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2">
            <IconButton
              onClick={handleRefresh}
              className="hover:bg-gray-100 transition-all duration-200"
              disabled={isRefreshing}
              title="Refresh">
              <RotateCw
                size={18}
                className={`text-gray-600 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />
            </IconButton>
            <IconButton
              onClick={handleSettingsClick}
              className="hover:bg-gray-100 transition-all duration-200"
              title="Column Settings">
              <Settings size={18} className="text-gray-600" />
            </IconButton>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="px-6 py-4 flex items-center justify-between">
        {/* Tabs on the left */}
        <div>
          <div className="flex items-center gap-1 bg-gray-100/80 p-1 rounded-xl">
            <button
              onClick={() => setActiveTab("unpaid")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 
                       ${
                         activeTab === "unpaid"
                           ? "bg-white text-[#5652B0] shadow-sm ring-1 ring-black/5"
                           : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                       }`}>
              Unpaid Inwards
            </button>
            <button
              onClick={() => setActiveTab("paid")}
              className={`px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 
                       ${
                         activeTab === "paid"
                           ? "bg-white text-[#5652B0] shadow-sm ring-1 ring-black/5"
                           : "text-gray-600 hover:text-gray-900 hover:bg-white/50"
                       }`}>
              Paid Inwards
            </button>
          </div>
        </div>

        {/* Filters on the right */}
        <div className="flex items-center gap-3">
          {/* Date Range Filter */}
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                  borderWidth: "1px",
                },
              },
            }}>
            <InputLabel>Date Range</InputLabel>
            <Select
              value={filters.dateRange}
              label="Date Range"
              onChange={e =>
                setFilters({ ...filters, dateRange: e.target.value })
              }>
              <MenuItem value="">All Time</MenuItem>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="yesterday">Yesterday</MenuItem>
              <MenuItem value="last7days">Last 7 Days</MenuItem>
              <MenuItem value="last30days">Last 30 Days</MenuItem>
              <MenuItem value="thisMonth">This Month</MenuItem>
            </Select>
          </FormControl>

          {/* Sort By Filter */}
          <FormControl
            size="small"
            sx={{
              minWidth: 180,
              "& .MuiOutlinedInput-root": {
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E5E7EB",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#5652B0",
                  borderWidth: "1px",
                },
              },
            }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={filters.sortBy}
              label="Sort By"
              onChange={e =>
                setFilters({ ...filters, sortBy: e.target.value })
              }>
              {sortOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Clear Filters Button */}
          {(searchQuery ||
            Object.values(filters).some(value => value !== "")) && (
            <button
              onClick={clearAllFilters}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700
                     bg-gray-50 hover:bg-gray-100 rounded-lg transition-all duration-200
                     border border-gray-200 hover:border-gray-300 active:bg-gray-200">
              <FilterX size={16} />
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {/* Table Section */}
      <div className="px-6">
        <Paper
          sx={{
            width: "100%",
            overflow: "hidden",
            boxShadow: "none",
            border: "1px solid #E5E7EB",
            borderRadius: "0.5rem",
          }}>
          <TableContainer
            sx={{ maxHeight: "calc(100vh - 300px)" }}
            className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-50">
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  {defaultColumns
                    .filter(column => visibleColumns[column.id])
                    .map(column => (
                      <TableCell
                        key={column.id}
                        align="center"
                        sx={{
                          backgroundColor: "#F9FAFB",
                          fontWeight: "600",
                          fontSize: "14px",
                          color: "#374151",
                          borderBottom: "2px solid #E5E7EB",
                        }}
                        className="font-outfit">
                        {column.label}
                      </TableCell>
                    ))}
                  <TableCell
                    align="center"
                    sx={{
                      backgroundColor: "#F9FAFB",
                      fontWeight: "600",
                      fontSize: "14px",
                      color: "#374151",
                      borderBottom: "2px solid #E5E7EB",
                    }}
                    className="font-outfit">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inwards
                  .filter(inward => inward.status === activeTab)
                  .map(inward => (
                    <TableRow
                      hover
                      key={inward.id}
                      sx={{
                        "&:hover": {
                          backgroundColor: "#F9FAFB",
                        },
                      }}>
                      {visibleColumns.date && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                          className="font-outfit">
                          {inward.date} {inward.month}
                        </TableCell>
                      )}
                      {visibleColumns.inwardId && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                            fontWeight: 500,
                          }}
                          className="font-outfit">
                          {inward.id}
                        </TableCell>
                      )}
                      {visibleColumns.poId && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                            fontWeight: 500,
                          }}
                          className="font-outfit">
                          {inward.poId}
                        </TableCell>
                      )}
                      {visibleColumns.supplier && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                          className="font-outfit">
                          {inward.supplier}
                        </TableCell>
                      )}
                      {visibleColumns.challanNo && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                          className="font-outfit">
                          {inward.challanNo}
                        </TableCell>
                      )}
                      {visibleColumns.amount && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                          className="font-outfit">
                          ₹{inward.amount.toLocaleString()}
                        </TableCell>
                      )}
                      {visibleColumns.paymentDue && activeTab === "unpaid" && (
                        <TableCell
                          align="center"
                          sx={{
                            padding: "12px 16px",
                            fontSize: "14px",
                            color: "#4B5563",
                          }}
                          className="font-outfit">
                          ₹{inward.paymentDue.toLocaleString()}
                        </TableCell>
                      )}
                      <TableCell
                        align="center"
                        sx={{
                          padding: "12px 16px",
                          fontSize: "14px",
                        }}
                        className="font-outfit">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors"
                            onClick={e => {
                              e.stopPropagation();
                              navigate(
                                `/purchase/inward/create?id=${inward.id}`,
                              );
                            }}
                            title="Edit">
                            <Pencil size={16} className="text-[#5652B0]" />
                          </button>
                          {inward.status === "unpaid" && (
                            <>
                              <button
                                className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors"
                                onClick={e => {
                                  e.stopPropagation();
                                  // Handle add payment
                                }}
                                title="Add Payment">
                                <Wallet size={16} className="text-[#5652B0]" />
                              </button>
                              <button
                                className="p-1.5 rounded-md bg-purple-50 hover:bg-purple-100 transition-colors"
                                onClick={e => handleReminderClick(e, inward)}
                                title="Set Reminder">
                                <Bell size={16} className="text-[#5652B0]" />
                              </button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                {inwards.filter(o => o.status === activeTab).length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={
                        Object.values(visibleColumns).filter(Boolean).length + 1
                      }
                      align="center"
                      sx={{
                        padding: "16px",
                        fontSize: "14px",
                        color: "#6B7280",
                      }}
                      className="font-outfit">
                      No {activeTab} purchase inwards found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </div>

      {/* Add Reminder Dialog */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Dialog
          open={reminderModalOpen}
          onClose={() => {
            setReminderModalOpen(false);
            setSelectedInwardForReminder(null);
            setReminderDateTime(null);
            setReminderDescription("");
          }}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            style: {
              borderRadius: "12px",
            },
          }}>
          <DialogTitle className="flex justify-between items-center border-b pb-4">
            <div className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-[#5652B0]" />
              <span className="text-xl font-semibold text-gray-800">
                Set Reminder
              </span>
            </div>
            <button
              onClick={() => {
                setReminderModalOpen(false);
                setSelectedInwardForReminder(null);
                setReminderDateTime(null);
                setReminderDescription("");
              }}
              className="text-gray-500 hover:text-gray-700 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </DialogTitle>
          <DialogContent>
            <div className="mt-6 flex flex-col gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-500 mb-1">
                  Purchase Inward
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800">
                    {selectedInwardForReminder?.id}
                  </span>
                  <span className="font-bold text-gray-800">
                    {selectedInwardForReminder?.supplier}
                  </span>
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  PO ID:{" "}
                  <span className="text-gray-800">
                    {selectedInwardForReminder?.poId}
                  </span>
                </div>
              </div>

              <DateTimePicker
                label="Reminder Date & Time"
                value={reminderDateTime}
                onChange={newValue => setReminderDateTime(newValue)}
                className="w-full"
                slotProps={{
                  textField: {
                    variant: "outlined",
                    fullWidth: true,
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                        "&:hover fieldset": {
                          borderColor: "#5652B0",
                        },
                      },
                      "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#5652B0",
                        },
                    },
                  },
                }}
              />

              <TextField
                label="Reminder Description"
                multiline
                rows={3}
                value={reminderDescription}
                onChange={e => setReminderDescription(e.target.value)}
                placeholder="Add a note about this reminder..."
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    "&:hover fieldset": {
                      borderColor: "#5652B0",
                    },
                  },
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: "#5652B0",
                    },
                }}
              />
            </div>
          </DialogContent>
          <DialogActions className="border-t p-4 gap-3">
            <Button
              onClick={() => {
                setReminderModalOpen(false);
                setSelectedInwardForReminder(null);
                setReminderDateTime(null);
                setReminderDescription("");
              }}
              variant="outlined"
              sx={{
                borderColor: "#5652B0",
                color: "#5652B0",
                "&:hover": {
                  borderColor: "#4A47A3",
                  backgroundColor: "rgba(86, 82, 176, 0.04)",
                },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
              }}>
              Cancel
            </Button>
            <Button
              onClick={handleSetReminder}
              variant="contained"
              disabled={!reminderDateTime}
              sx={{
                bgcolor: "#5652B0",
                "&:hover": {
                  bgcolor: "#4A47A3",
                },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: "#4A47A3",
                  boxShadow: "none",
                },
              }}>
              Set Reminder
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </div>
  );
};

export default PurchaseInward;
